import { NotificationInfo } from '@kp/notifications/models/notification-info.model';
import { CardItemState } from '@kp/shared/components/cards/card-category/enums/card-item-state.enum';
import { Habit } from '@kp/shared/components/cards/card-category/models/habit.model';
import { SavedCardState } from '@kp/shared/components/cards/card-category/models/saved-card-state.model';
import { Deserializable, JsonObject, Serializable } from '@ktypes/models';
import cloneDeep from 'lodash/cloneDeep';

export enum UserCardPreference {
  MORE,
  LESS,
  NEUTRAL,
}

export class UserCardState implements Deserializable<any, UserCardState>, Serializable<JsonObject> {
  constructor(
    public preference?: UserCardPreference,
    public isTracking = false,
    public isRepeating = false,
    public isRecommended = false,
    public completedId?: string,
    public completedCount?: number,
    public availableItemCount?: number,
    public completedItemCount?: number,
    public isQuestComplete?: boolean,
    public likeCount?: number,
    // UI specific states
    public shownAnimation?: boolean,
    public trackingJustChanged = false,
    public currentState?: CardItemState,
    public saveState?: SavedCardState,
    public notifications?: NotificationInfo[],
    public habits?: Habit[]
  ) {}

  deserialize(json: any): UserCardState {
    if (json == null) {
      return null;
    }

    const tracking = json['tracking'] || json || {};
    this.preference =
      json['preference']?.endsWith?.('_more') || json['preference'] === UserCardPreference.MORE
        ? UserCardPreference.MORE
        : UserCardPreference.NEUTRAL;
    this.isTracking = tracking['isTracking'];
    this.isRepeating = tracking['isRepeating'];
    this.isRecommended = tracking['isRecommended'];
    // Need to set to null if isQuestComplete is undefined due to quest tab sorting of boolean
    this.isQuestComplete = tracking['isQuestComplete'] || null;
    this.likeCount = json['likeCount'];
    this.completedId = tracking['completedId'];
    this.completedCount = tracking['completedCount'];
    this.availableItemCount = tracking['availableItemCount'];
    this.completedItemCount = tracking['completedItemCount'];
    this.shownAnimation = checkCompletedId(this.completedId);
    this.currentState = this.isTracking
      ? checkCompletedId(this.completedId)
        ? CardItemState.ACT_COMPLETE
        : CardItemState.ACT_TO_DO
      : CardItemState.DISCOVER_DEFAULT;
    this.saveState = new SavedCardState().deserialize(json['saveState'] || {});
    this.habits = json['habits']?.map((habit) => new Habit().deserialize(habit));
    this.notifications = json['notifications']?.map((notification) => new NotificationInfo().deserialize(notification));
    return this;
  }

  clone(card: CardItemState) {
    return cloneDeep(card);
  }

  serialize() {
    return {
      preference:
        this.preference === UserCardPreference.MORE
          ? 'card_more'
          : this.preference === UserCardPreference.LESS
          ? 'card_less'
          : null,
      likeCount: this.likeCount,
      tracking: {
        isTracking: this.isTracking,
        completedId: this.completedId,
        isRepeating: this.isRepeating,
        isQuestComplete: this.isQuestComplete,
        isRecommended: this.isRecommended,
        completedCount: this.completedCount,
        availableItemCount: this.availableItemCount,
        completedItemCount: this.completedItemCount,
      },
      saveState: this.saveState.serialize(),
      habits: this.habits.map((habit) => habit.serialize()),
      notifications: this.notifications.map((notification) => notification.serialize()),
    };
  }

  get isTransitioningFromToDoState() {
    return this.isCompleted && [CardItemState.ACT_ANIMATION].includes(this.currentState);
  }

  get isCompleted() {
    // Completed quest cards have isQuestComplete boolean but completedId set to null
    // Other completed cards have completedIds
    return checkCompletedId(this.completedId) || this.isQuestComplete;
  }
}

function checkCompletedId(completedId: any): boolean {
  return completedId && completedId !== '' && completedId !== 'None';
}
